import { OASFormElement } from '@oas-ui/core'
import { computePosition, getViewport, type Placement } from '../../overlay/floating/index.js'
import { resolveDirection } from '../../shared/direction.js'
// 注册 oas-virtual-list（OASVirtualList 仅作类型用，需裸 import 保住注册副作用）
import '../../data/virtual-list/index.js'
import type { OASVirtualList } from '../../data/virtual-list/index.js'

interface Option {
  label: string
  value: string
  disabled?: boolean
  /** 选项分组标题：同一组连续渲染组标题（不可选），组内选项缩进 */
  group?: string
}

const VALID_SIZES = ['small', 'medium', 'large'] as const
const VALID_STATUSES = ['error', 'warning', 'success'] as const
/** variant 形态（对齐 input 三值变体：默认 outlined 走基础样式） */
const VALID_VARIANTS = ['outlined', 'filled', 'borderless'] as const

/** 枚举归一化：合法值原样返回，空/非法值静默回落默认 */
function normalizeChoice(raw: string, fallback: string, valid: readonly string[]): string {
  if (raw === '') return fallback
  return (valid as readonly string[]).includes(raw) ? raw : fallback
}

/** 12 向 placement 词表校验（对齐 dropdown/浮层引擎：基向 + -start/-end 逻辑对齐后缀）；非法回落 bottom-start */
function normalizePlacement(raw: string): Placement {
  const m = /^(top|bottom|left|right)(?:-(start|end))?$/.exec(raw.trim())
  return (m ? m[0] : 'bottom-start') as Placement
}

/** 选项行样式（非虚拟模式渲染在组件自身 shadow；虚拟模式需注入到 vlist shadow，两处共用） */
const OPTION_STYLE = `
.option {
  padding: var(--oas-space-2) var(--oas-space-3);
  border-radius: var(--oas-radius-sm);
  cursor: pointer;
  font-size: var(--oas-font-size-md);
  color: var(--oas-color-text-primary);
}
.option:hover,
.option.active {
  background: var(--oas-color-primary);
  color: var(--oas-color-text-on-primary);
}
.option.grouped {
  padding-inline-start: calc(var(--oas-space-3) + var(--oas-space-4));
}
.option[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}
`

/** 虚拟模式注入 oas-virtual-list 的 shadow：选项行占满 item、整行可高亮（对齐 select） */
const VIRTUAL_ROW_STYLE = `
[part="item"] {
  display: flex;
  align-items: center;
}
[part="item"] .option {
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}
${OPTION_STYLE}
`

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
  width: 240px;
  /* 面板高度 CSS 变量开口：宿主覆盖即可调高（默认 240px），不占属性 API（对齐 select） */
  --oas-auto-complete-dropdown-height: 240px;
}
:host([hidden]) {
  display: none;
}
.wrapper {
  position: relative;
}
input {
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-3);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  transition: border-color var(--oas-transition-fast) var(--oas-ease-out),
    box-shadow var(--oas-transition-fast) var(--oas-ease-out);
}
input:hover {
  border-color: var(--oas-color-primary);
}
input:focus {
  outline: none;
  border-color: var(--oas-color-primary);
  box-shadow: var(--oas-focus-ring);
}
/* ---- size 尺寸档位（默认 medium 走基础样式；高度对齐 --oas-control-height-* token） ---- */
:host([data-size='small']) input {
  height: var(--oas-control-height-sm);
  font-size: var(--oas-font-size-sm);
}
:host([data-size='large']) input {
  height: var(--oas-control-height-lg);
  font-size: var(--oas-font-size-lg);
}
/* ---- status 校验态：success / warning / error（error 兼容宿主 aria-invalid 通道） ---- */
:host([data-status='success']) input {
  border-color: var(--oas-color-success);
}
:host([data-status='success']) input:focus {
  border-color: var(--oas-color-success);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-success) 30%, transparent);
}
:host([data-status='warning']) input {
  border-color: var(--oas-color-warning);
}
:host([data-status='warning']) input:focus {
  border-color: var(--oas-color-warning);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-warning) 30%, transparent);
}
:host([data-status='error']) input {
  border-color: var(--oas-color-danger);
}
:host([data-status='error']) input:focus {
  border-color: var(--oas-color-danger);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-danger) 30%, transparent);
}
:host([aria-invalid='true']) input {
  border-color: var(--oas-color-danger);
}
:host([aria-invalid='true']) input:focus {
  border-color: var(--oas-color-danger);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--oas-color-danger) 30%, transparent);
}
input:disabled {
  cursor: not-allowed;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
}
input:disabled:hover {
  border-color: var(--oas-color-border);
}
/* ---- variant 形态：filled 填充 / borderless 无框（默认 outlined 走基础样式，对齐 input） ---- */
:host([data-variant='filled']) input {
  border-color: transparent;
  background: var(--oas-color-bg-hover);
}
:host([data-variant='filled']) input:hover {
  border-color: var(--oas-color-border);
}
:host([data-variant='filled']) input:focus {
  border-color: var(--oas-color-primary);
  box-shadow: var(--oas-focus-ring);
}
:host([data-variant='filled']) input:disabled {
  background: var(--oas-color-bg-disabled);
}
:host([data-variant='borderless']) input {
  border-color: transparent;
  background: transparent;
}
:host([data-variant='borderless']) input:hover {
  border-color: transparent;
}
:host([data-variant='borderless']) input:focus {
  border-color: transparent;
  box-shadow: none;
}
:host([data-variant='borderless']) input:disabled {
  background: transparent;
}
/* clearable 时给清空按钮让位 */
:host([clearable]) input {
  padding-inline-end: var(--oas-space-8, 40px);
}
.clear-btn {
  position: absolute;
  inset-inline-end: var(--oas-space-2);
  top: 50%;
  transform: translateY(-50%);
  appearance: none;
  border: none;
  background: transparent;
  padding: 2px;
  cursor: pointer;
  color: var(--oas-color-text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 2;
}
.clear-btn:hover {
  color: var(--oas-color-text-primary);
}
.clear-btn:focus-visible {
  outline: none;
  box-shadow: var(--oas-focus-ring);
}
.clear-btn[hidden] {
  display: none;
}
.clear-btn svg {
  width: 12px;
  height: 12px;
  display: block;
}
/* 复用浮层定位引擎：fixed + computePosition 锚定 input 下方（逃出 overflow 祖先），
   空间不足自动翻转避让——与 select/combobox 的定位契约一致 */
.dropdown {
  position: fixed;
  z-index: calc(var(--oas-z-index-base, 0) + var(--oas-z-dropdown, 1000));
  background: var(--oas-color-bg);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  box-shadow: var(--oas-shadow-md);
  /* 液态玻璃接线（容器级 surface）：变量缺省时零影响 */
  backdrop-filter: var(--oas-glass-blur, none);
  -webkit-backdrop-filter: var(--oas-glass-blur, none);
  outline: 1px solid var(--oas-glass-ring, transparent);
  outline-offset: -1px;
  padding: var(--oas-space-1);
  display: none;
}
.dropdown.open {
  display: block;
}
.panel-header {
  padding: var(--oas-space-2) var(--oas-space-3) var(--oas-space-1);
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
}
.panel-footer {
  padding: var(--oas-space-1) var(--oas-space-3) var(--oas-space-2);
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
}
.listbox {
  max-height: var(--oas-auto-complete-dropdown-height, 240px);
  overflow-y: auto;
}
.option-group {
  padding: var(--oas-space-2) var(--oas-space-3) var(--oas-space-1);
  font-size: var(--oas-font-size-sm);
  color: var(--oas-color-text-secondary);
  user-select: none;
  cursor: default;
}
${OPTION_STYLE}
.empty {
  padding: var(--oas-space-3);
  text-align: center;
  color: var(--oas-color-text-secondary);
  font-size: var(--oas-font-size-sm);
}
`

export class OASAutoComplete extends OASFormElement {
  /** 原生表单集成（label for / FormData / reset / fieldset disabled）；沿静态原型链已可继承，显式声明便于阅读与检索 */
  static override formAssociated = true

  static override get observedAttributes(): string[] {
    return [
      'value',
      'placeholder',
      'options',
      'disabled',
      'clearable',
      'loading',
      'debounce',
      'trigger-on-focus',
      'size',
      'variant',
      'status',
      'readonly',
      'disabled-skip',
      // 面板 12 向放置（对齐浮层定位引擎；非法值回落 bottom-start）
      'placement',
      // 转发内层 input（原生 autofocus 不穿透 shadow）
      'autofocus',
      // required 仅驱动原生校验链（valueMissing）
      'required',
      // 能力缺口 D3：选项虚拟滚动（对齐 select 契约；virtual + item-height 定高）
      'virtual',
      'item-height',
    ]
  }

  /**
   * @apiProperty 当前值（公开读通道，对齐原生 `input.value`）：内部 input 在场读其当前文本
   * （自由文本组件以可见文本为准），否则回落 `value` 属性，与 getFormValue()/FormData 口径一致。
   */
  get value(): string {
    return this.input ? this.input.value : this.getAttr('value', '')
  }

  /**
   * 程序性写值（受控赋值即生效语义）：写受控 `value` 属性并清掉键入过滤词（`query`
   * 是 update 内「属性 → 内部控件」应用路径的守卫，不清理会跳过回写），再显式强制
   * update 兜底——同值 `setAttribute` 也会触发 attributeChangedCallback（Chromium 实测），
   * 显式调用仅作保险，赋值在任何情况下都生效。
   * 不派发任何事件。
   */
  set value(v: string) {
    const next = v == null ? '' : String(v)
    this.setAttribute('value', next)
    this.query = ''
    if (this.hasRendered) this.update()
  }

  private input: HTMLInputElement | null = null
  private dropdown: HTMLElement | null = null
  private listbox: HTMLElement | null = null
  private vlist: OASVirtualList | null = null
  private clearBtn: HTMLButtonElement | null = null
  private _options: Option[] = []
  /** 初始 value 基线（form.reset 恢复目标）：初始渲染/受控写入跟随 value 属性刷新 */
  private initialValue = ''
  /** 用户交互脏标记（选择建议/清空等会写回 value 属性的路径置位）：置位后基线冻结，reset 恢复基线并清脏 */
  private valueDirty = false

  /** Vue/React 会把 options 识别为实例属性走 property 赋值；setter 反射到 attribute 统一解析链路 */
  get options(): Option[] {
    return this._options
  }
  set options(value: Option[] | string) {
    this.setAttribute('options', typeof value === 'string' ? value : JSON.stringify(value))
  }
  private activeIndex = 0
  private query = ''
  private openState = false
  /** IME 组合态：composition 期间不触发过滤与键盘选中（中文输入刚需） */
  private composing = false
  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="wrapper" part="wrapper">
        <input part="input" role="combobox" aria-haspopup="listbox" aria-autocomplete="list"
          aria-expanded="false" aria-controls="ac-list" autocomplete="off" />
        <button class="clear-btn" part="clear" type="button" hidden aria-label="">
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4 4 L12 12 M12 4 L4 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="dropdown" part="dropdown">
          <div class="panel-header" part="header" hidden><slot name="header"></slot></div>
          <div class="listbox" part="listbox" role="listbox" id="ac-list"></div>
          <oas-virtual-list class="vlist" part="virtual-list" hidden></oas-virtual-list>
          <div class="panel-footer" part="footer" hidden><slot name="footer"></slot></div>
        </div>
      </div>
    `
  }

  /** 缓存节点引用 + 绑定输入/组合态/键盘/焦点/清空/外部点击事件（render 与水合路径共用） */
  private bind(): void {
    this.input = this.shadow.querySelector('input')
    this.dropdown = this.shadow.querySelector('.dropdown')
    this.listbox = this.shadow.querySelector('.listbox')
    this.vlist = this.shadow.querySelector<OASVirtualList>('oas-virtual-list')
    this.clearBtn = this.shadow.querySelector('.clear-btn')

    this.input?.addEventListener('compositionstart', () => {
      this.composing = true
    })
    this.input?.addEventListener('compositionend', () => {
      this.composing = false
      // 组合结束后以最终文本补发一次过滤（组合期间的 input 不派发）
      this.scheduleFilter()
    })
    this.input?.addEventListener('input', () => this.handleInput())
    this.input?.addEventListener('keydown', (e: KeyboardEvent) => this.handleKey(e))
    this.input?.addEventListener('focus', () => {
      // 对齐 oas-input 契约：聚焦/失焦派发，detail 携带当前输入文本（不做禁用/只读守卫）
      this.emit('focus', { value: this.input?.value ?? '' })
      this.handleFocus()
    })
    this.input?.addEventListener('blur', () => {
      this.emit('blur', { value: this.input?.value ?? '' })
      // 自由文本组件：失焦只收起建议，不回退输入内容
      if (this.openState) this.renderDropdown(false)
    })
    // 点击面板/清空按钮不触发 input blur：mousedown 里 preventDefault 阻止默认失焦
    this.dropdown?.addEventListener('mousedown', (e: MouseEvent) => e.preventDefault())
    this.dropdown?.addEventListener('click', (e: MouseEvent) => {
      const row = (e.target as Element).closest('[role="option"]')
      if (!row) return
      const idx = Number(row.getAttribute('data-index'))
      const option = this.filtered()[idx]
      if (option && !option.disabled) this.choose(option)
    })
    this.clearBtn?.addEventListener('mousedown', (e: MouseEvent) => e.preventDefault())
    this.clearBtn?.addEventListener('click', (e: MouseEvent) => {
      e.stopPropagation()
      this.clearValue()
    })
    // 虚拟滚动：复用 oas-virtual-list 的窗口计算，把每个可见项渲染为选项行（对齐 select）
    this.vlist?.addEventListener('oas-item', ((
      e: CustomEvent<{ index: number; item: Option; element: HTMLElement }>,
    ) => {
      const detail = e.detail
      if (detail && detail.item && detail.element) {
        this.createOptionRow(detail.item, detail.index, detail.element)
      }
    }) as EventListener)
    // autofocus：转发到内部 input（原生 autofocus 不穿透 shadow，挂载后手动聚焦一次，同 input/button）
    if (this.hasAttr('autofocus')) {
      queueMicrotask(() => this.input?.focus())
    }
    this.onCleanup(() => {
      document.removeEventListener('click', this.handleOutsideClick, true)
      this.clearDebounce()
    })
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（输入框/下拉/listbox 存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('input')) return false
    if (!this.shadow.querySelector('.dropdown')) return false
    if (!this.shadow.querySelector('.listbox')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    this.parseOptions()
    if (!this.input) return
    const value = this.getAttr('value', '')
    // form.reset 恢复基线：初始渲染/受控写入（非用户交互的属性变化）跟随 value 属性刷新；
    // 用户交互（选择建议/清空）置脏后基线冻结
    if (!this.valueDirty) this.initialValue = value
    const placeholder = this.getAttr('placeholder', '')
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入）
    const disabled = this.injectDisabled()
    // 尺寸/校验态镜像（size 就近读取 config-provider 注入，与全局密度联动）
    const size = normalizeChoice(this.injectValue('size', 'medium'), 'medium', VALID_SIZES)
    this.setAttribute('data-size', size)
    // variant 形态镜像（对齐 input 三值变体：outlined/filled/borderless）
    const variant = normalizeChoice(this.getAttr('variant', ''), 'outlined', VALID_VARIANTS)
    this.setAttribute('data-variant', variant)
    const status = normalizeChoice(this.getAttr('status', ''), '', VALID_STATUSES)
    if (status) this.setAttribute('data-status', status)
    else this.removeAttribute('data-status')
    if (this.input.value !== value && this.query === '') this.input.value = value
    this.input.placeholder = placeholder
    // 无障碍名：宿主 aria-label > aria-labelledby > placeholder > locale 默认名（combobox 输入必须有名，axe: label）；
    // 宿主显式设置被保留，aria-labelledby 移除后残留同步清理
    const hostAriaLabel = this.getAttribute('aria-label')
    const hostLabelledby = this.getAttribute('aria-labelledby')
    if (hostAriaLabel != null && hostAriaLabel !== '') {
      this.input.setAttribute('aria-label', hostAriaLabel)
      this.input.removeAttribute('aria-labelledby')
    } else if (hostLabelledby != null && hostLabelledby !== '') {
      this.input.setAttribute('aria-labelledby', hostLabelledby)
      this.input.removeAttribute('aria-label')
    } else {
      this.input.removeAttribute('aria-labelledby')
      this.input.setAttribute('aria-label', placeholder || this.t('autoComplete.defaultLabel'))
    }
    this.input.disabled = disabled
    this.input.readOnly = this.hasAttr('readonly')
    if (status === 'error') this.input.setAttribute('aria-invalid', 'true')
    else this.input.removeAttribute('aria-invalid')
    this.syncClear()
    // 下拉展开时同步刷新：options / loading / locale 文案 / 面板插槽变化即时反映
    if (this.openState) this.renderDropdown(true)

    // 原生表单数据 + 校验链同步（form-associated；无 name 浏览器自动不提交）
    this.syncFormValue()
    this.syncValidity()
  }

  /** 表单值快照（照原生 input 语义：当前输入文本；render 前读 value 属性） */
  protected override getFormValue(): string | null {
    return this.input ? this.input.value : this.getAttr('value', '')
  }

  /** 原生校验链同步：required 且文本为空 → valueMissing（flag 为 true 时 message 按 Chromium 契约必须非空） */
  private syncValidity(): void {
    if (this.hasAttr('required') && this.getFormValue() === '') {
      this.setValidity({ valueMissing: true }, this.t('form.valueMissing'))
    } else {
      this.setValidity({})
    }
  }

  /** 表单 reset：恢复初始基线文本（清脏 + 按基线恢复 value 属性与输入回显），不派发事件（与原生 reset 一致） */
  protected override resetFormValue(): void {
    this.valueDirty = false
    this.clearDebounce()
    this.query = ''
    if (this.initialValue === '') this.removeAttribute('value')
    else this.setAttribute('value', this.initialValue)
    if (this.input) this.input.value = this.initialValue
    this.syncClear()
    this.syncFormValue()
    this.syncValidity()
  }

  private parseOptions(): void {
    try {
      const parsed = JSON.parse(this.getAttr('options', '[]'))
      this._options = Array.isArray(parsed) ? parsed.filter((o): o is Option => o && typeof o.value === 'string') : []
    } catch {
      this._options = []
    }
  }

  private filtered(): Option[] {
    const q = this.query.trim().toLowerCase()
    if (!q) return this._options
    return this._options.filter((o) => o.label.toLowerCase().includes(q))
  }

  /** 清空按钮可见性：clearable && 未禁用 && 输入框有内容（自由文本组件以可见文本为准） */
  private syncClear(): void {
    if (!this.clearBtn || !this.input) return
    this.clearBtn.setAttribute('aria-label', this.t('input.clear'))
    this.clearBtn.hidden = !(this.hasAttr('clearable') && !this.injectDisabled() && this.input.value !== '')
  }

  // ---- 输入链路（IME 守卫 + debounce） ----

  private handleInput(): void {
    if (!this.input) return
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    this.query = this.input.value
    // 照原生 input 语义：FormData 提交当前输入文本（不随 debounce 延迟；debounce 只影响过滤与派发）
    this.syncFormValue()
    this.syncValidity()
    this.syncClear()
    this.scheduleFilter()
  }

  /** debounce 防抖值（ms，默认 0 不防抖）：只防抖 oas-input 派发与过滤触发，不防抖输入回显 */
  private debounceDelay(): number {
    const raw = this.getAttr('debounce', '0')
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) || n < 0 ? 0 : n
  }

  private clearDebounce(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  private scheduleFilter(): void {
    // IME 组合中不派发不过滤（组合期 input 是拼音中间态）
    if (this.composing) return
    this.clearDebounce()
    const delay = this.debounceDelay()
    if (delay <= 0) {
      this.applyFilter()
      return
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.applyFilter()
    }, delay)
  }

  private applyFilter(): void {
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    this.emit('input', { value: this.query })
    this.activeIndex = 0
    this.renderDropdown(true)
  }

  // ---- 焦点 / 键盘 ----

  private handleFocus(): void {
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    // trigger-on-focus 默认 false：聚焦不弹全量建议（维持「输入优先」现状），显式开启走 datalist 心智
    if (this.hasAttr('trigger-on-focus')) {
      this.activeIndex = 0
      this.renderDropdown(true)
    }
  }

  private handleKey(e: KeyboardEvent): void {
    if (this.injectDisabled() || this.hasAttr('readonly')) return
    // IME 组合期：Enter/方向键交给输入法（确认候选/移动候选光标），不触发选中与导航
    if (this.composing || e.isComposing) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!this.openState) {
        this.activeIndex = 0
        this.renderDropdown(true)
      } else {
        this.moveActive(1)
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!this.openState) {
        this.activeIndex = 0
        this.renderDropdown(true)
      } else {
        this.moveActive(-1)
      }
    } else if (e.key === 'Enter') {
      if (!this.openState) return
      const list = this.filtered()
      if (list.length === 0) return
      e.preventDefault()
      const option = list[this.activeIndex]
      if (option && !option.disabled) this.choose(option)
    } else if (e.key === 'Escape') {
      if (this.openState) this.renderDropdown(false)
    }
  }

  /** 键盘移动高亮：增量同步（不重建 DOM）+ 滚动跟随（对齐 select 机制） */
  private moveActive(dir: 1 | -1): void {
    const n = this.filtered().length
    if (n === 0) return
    this.activeIndex = (this.activeIndex + dir + n) % n
    this.syncActive()
    this.scrollActiveIntoView()
  }

  /** 虚拟滚动定高：默认 36（与 oas-virtual-list 默认一致，匹配选项行视觉高度；对齐 select） */
  private virtualItemHeight(): number {
    const raw = this.getAttr('item-height', '36')
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) ? 36 : n
  }

  /** 面板高度：--oas-auto-complete-dropdown-height CSS 变量开口（默认 240px，对齐 select）。
   *  computed 优先（覆盖继承/类样式等场景），inline style 兜底（宿主直设 host 内联的主通道） */
  private dropdownHeight(): number {
    const raw =
      getComputedStyle(this).getPropertyValue('--oas-auto-complete-dropdown-height').trim() ||
      this.style.getPropertyValue('--oas-auto-complete-dropdown-height').trim()
    const n = Number.parseInt(raw, 10)
    return Number.isNaN(n) || n <= 0 ? 240 : n
  }

  /** 虚拟/非虚拟显隐切换：虚拟时选项窗口在 vlist，非虚拟在 listbox */
  private setVirtualVisible(visible: boolean): void {
    if (this.listbox) this.listbox.hidden = visible
    if (this.vlist) this.vlist.hidden = !visible
  }

  private scrollActiveIntoView(): void {
    // 虚拟滚动：让 activeIndex 所在项进入视口（设置 scrollTop，vlist 随 scroll 重算窗口）
    const vlist = this.vlist
    if (vlist && !vlist.hidden) {
      const ih = this.virtualItemHeight()
      const vp = vlist.shadowRoot?.querySelector<HTMLElement>('.viewport')
      if (vp) {
        const top = this.activeIndex * ih
        const vh = vp.clientHeight || 240
        const cur = vp.scrollTop
        if (top < cur) vp.scrollTop = Math.max(0, top)
        else if (top + ih > cur + vh) vp.scrollTop = Math.max(0, top + ih - vh)
        return
      }
    }
    if (!this.listbox) return
    const row = this.listbox.querySelector<HTMLElement>(`#opt-${this.activeIndex}`)
    if (!row) return
    const top = row.offsetTop
    const bottom = row.offsetTop + row.offsetHeight
    const vh = this.listbox.clientHeight
    const cur = this.listbox.scrollTop
    if (top < cur) this.listbox.scrollTop = Math.max(0, top)
    else if (bottom > cur + vh) this.listbox.scrollTop = Math.max(0, bottom - vh)
  }

  // ---- 下拉渲染 ----

  /** header/footer 插槽可见性：light DOM 提供 [slot=...] 内容时显示（每次展开时同步） */
  private syncPanelSlots(): void {
    const header = this.shadow.querySelector<HTMLElement>('.panel-header')
    const footer = this.shadow.querySelector<HTMLElement>('.panel-footer')
    if (header) header.hidden = !this.querySelector('[slot="header"]')
    if (footer) footer.hidden = !this.querySelector('[slot="footer"]')
  }

  private renderDropdown(open: boolean): void {
    if (!this.dropdown || !this.input || !this.listbox) return
    this.openState = open
    if (!open) {
      this.dropdown.classList.remove('open')
      this.input.setAttribute('aria-expanded', 'false')
      this.input.removeAttribute('aria-activedescendant')
      document.removeEventListener('click', this.handleOutsideClick, true)
      return
    }
    this.syncPanelSlots()
    this.listbox.innerHTML = ''
    this.setVirtualVisible(false)

    // loading 占位态：宿主请求远程建议期间显示加载文案（role=status 播报）
    if (this.hasAttr('loading')) {
      const status = document.createElement('div')
      status.className = 'empty'
      status.setAttribute('role', 'status')
      status.textContent = this.t('loading.loading')
      this.listbox.appendChild(status)
      this.finishOpen()
      return
    }

    const list = this.filtered()
    this.activeIndex = Math.min(this.activeIndex, Math.max(list.length - 1, 0))

    // 空态：自定义 [slot="empty"] 优先，缺省回落 locale 文案
    if (list.length === 0) {
      const empty = document.createElement('div')
      empty.className = 'empty'
      empty.setAttribute('role', 'status')
      const slot = document.createElement('slot')
      slot.setAttribute('name', 'empty')
      const def = document.createElement('span')
      def.className = 'empty-default'
      def.textContent = this.t('autoComplete.noMatch')
      if (this.querySelector('[slot="empty"]')) def.hidden = true
      empty.append(slot, def)
      this.listbox.appendChild(empty)
      this.finishOpen()
      return
    }

    // 虚拟滚动：大数据量时复用 oas-virtual-list 仅渲染可见窗口（对齐 select 契约）；
    // 带 group 的选项回退非虚拟全量渲染（组标题是不同行高的流式分隔，虚拟定高模型不适配）；
    // vlist 缺失（如手写 DSD 快照无此元素）时同样回退全量渲染，避免静默空下拉
    if (this.hasAttr('virtual') && this.vlist && !list.some((o) => o.group !== undefined)) {
      this.renderVirtualList(list)
      this.finishOpen()
      return
    }

    let prevGroup: string | undefined
    let idx = 0
    for (const option of list) {
      // 分组标题：组字段变化时插入（不可选，仅展示）
      if (option.group !== undefined && option.group !== prevGroup) {
        const groupEl = document.createElement('div')
        groupEl.className = 'option-group'
        groupEl.textContent = option.group
        this.listbox.appendChild(groupEl)
      }
      prevGroup = option.group
      this.createOptionRow(option, idx)
      idx++
    }
    this.finishOpen()
  }

  /** 虚拟模式：切到 vlist 渲染（注入行样式/视口键盘可达性由 input 键盘流负责）并喂入可见选项 */
  private renderVirtualList(visible: Option[]): void {
    const vlist = this.vlist
    if (!vlist) return
    this.setVirtualVisible(true)
    // 行样式注入 vlist shadow（虚拟行在 vlist shadow 内，组件自身样式够不到）
    const vlistRoot = vlist.shadowRoot
    if (vlistRoot && !vlistRoot.querySelector('style[data-oas-auto-complete-rows]')) {
      const style = document.createElement('style')
      style.setAttribute('data-oas-auto-complete-rows', '')
      style.textContent = VIRTUAL_ROW_STYLE
      vlistRoot.appendChild(style)
    }
    // 视口键盘可达性由 input 的 combobox 键盘流负责：走 viewportFocusable 公共通道关掉
    // （直接 removeAttribute 会被 vlist 下次 update 的 syncViewportFocusable 加回）
    vlist.viewportFocusable = false
    vlist.setAttribute('items-role', 'listbox')
    vlist.setAttribute('item-role', 'presentation')
    vlist.setAttribute('height', String(this.dropdownHeight()))
    vlist.setAttribute('item-height', String(this.virtualItemHeight()))
    vlist.items = visible
  }

  /** 展开收尾：面板显隐 / aria / 定位 / 全局点击监听（open 分支共用） */
  private finishOpen(): void {
    if (!this.dropdown || !this.input) return
    this.dropdown.classList.add('open')
    this.input.setAttribute('aria-expanded', 'true')
    this.syncActive()
    this.positionDropdown()
    document.addEventListener('click', this.handleOutsideClick, true)
  }

  /** 构建选项行：角色/aria/高亮/自定义渲染模板；非虚拟点击走 dropdown 委托，mousemove 走增量同步 */
  private createOptionRow(option: Option, idx: number, container?: HTMLElement): void {
    const parent = container ?? this.listbox
    if (!parent) return
    const row = document.createElement('div')
    row.className = 'option'
    if (option.group !== undefined) row.classList.add('grouped')
    row.setAttribute('part', 'option')
    row.setAttribute('role', 'option')
    row.setAttribute('aria-disabled', String(option.disabled ?? false))
    row.id = `opt-${idx}` // aria-activedescendant 锚点（shadow 内 id 作用域隔离，无宿主冲突）
    row.setAttribute('data-index', String(idx))
    if (idx === this.activeIndex) row.classList.add('active')
    const label = document.createElement('span')
    label.className = 'option-label'
    this.fillOptionLabel(label, option)
    row.appendChild(label)
    row.addEventListener('mousemove', () => {
      if (this.activeIndex === idx) return
      this.activeIndex = idx
      this.syncActive()
    })
    parent.appendChild(row)
    // 虚拟模式：行在 vlist shadow 内，dropdown 上的点击委托收不到（shadow 边界重定向 target），
    // 行上直挂点击；非虚拟仍走 dropdown 委托（避免双触发）
    if (container != null) {
      row.addEventListener('click', () => {
        if (option.disabled) return
        this.choose(option)
      })
    }
    // 自定义选项渲染：宿主可监听改写 element（图标/富文本），机制与 select 的 oas-option-render 一致
    this.emit('option-render', { index: idx, option, element: label })
  }

  /** 选项 label 渲染：template[slot="option"] 克隆 + [data-option-label] 绑定，缺省回落纯文本 */
  private fillOptionLabel(labelEl: HTMLElement, option: Option): void {
    const tpl = this.querySelector('template[slot="option"]')
    if (tpl instanceof HTMLTemplateElement) {
      labelEl.appendChild(tpl.content.cloneNode(true))
      const binder = labelEl.querySelector('[data-option-label]')
      if (binder) binder.textContent = option.label
    } else {
      labelEl.textContent = option.label
    }
  }

  /** 高亮/aria 增量同步（不重建 DOM）：虚拟滚动下窗口随 scroll 重算后行内 class 由 createOptionRow 落定 */
  private syncActive(): void {
    for (const row of this.renderedOptionRows()) {
      row.classList.toggle('active', Number(row.getAttribute('data-index')) === this.activeIndex)
    }
    const n = this.filtered().length
    if (this.openState && n > 0 && this.activeIndex < n) {
      this.input?.setAttribute('aria-activedescendant', `opt-${this.activeIndex}`)
    } else {
      this.input?.removeAttribute('aria-activedescendant')
    }
  }

  /** 已渲染的选项行：非虚拟在 listbox、虚拟在 vlist shadow（open shadow 可跨根查询） */
  private renderedOptionRows(): HTMLElement[] {
    const out: HTMLElement[] = []
    if (this.listbox) out.push(...this.listbox.querySelectorAll<HTMLElement>('.option[data-index]'))
    const vroot = this.vlist?.shadowRoot
    if (vroot) out.push(...vroot.querySelectorAll<HTMLElement>('.option[data-index]'))
    return out
  }

  /** 复用浮层定位引擎：锚定输入框，空间不足自动翻转/避让，宽度对齐输入框。
   *  placement 12 向（默认 bottom-start，逻辑 -start/-end 语义随书写方向镜像）：
   *  完整 placement 交给引擎，由引擎按 direction 镜像对齐并返回实际落位，
   *  data-placement 写引擎返回值（与 dropdown/date-picker 同口径，宿主可感知翻转结果）。 */
  private positionDropdown(): void {
    if (!this.dropdown || !this.input) return
    const anchorRect = this.input.getBoundingClientRect()
    // 先撑宽再测量/定位：dropdown 为 auto 宽度，撑宽前测会按固有宽度算 left → 首次展开偏右。
    this.dropdown.style.width = `${anchorRect.width}px`
    const panelRect = this.dropdown.getBoundingClientRect()
    const requested = normalizePlacement(this.getAttr('placement', 'bottom-start'))
    const { top, left, placement } = computePosition(anchorRect, panelRect, requested, getViewport(), 8, true, {
      direction: resolveDirection(this),
    })
    this.dropdown.style.top = `${top}px`
    this.dropdown.style.left = `${left}px`
    this.dropdown.setAttribute('data-placement', placement)
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    const path = e.composedPath()
    if (!path.includes(this) && !path.some((n) => n instanceof Node && this.shadow.contains(n))) {
      this.renderDropdown(false)
    }
  }

  // ---- 选中 / 清空 ----

  private choose(option: Option): void {
    this.clearDebounce()
    // 用户选择置脏：冻结 reset 基线（须在 setAttribute 之前，防止 update 把基线刷成新值）
    this.valueDirty = true
    this.query = option.label
    this.renderDropdown(false)
    if (this.input) this.input.value = option.label
    this.setAttribute('value', option.value)
    this.emit('change', { value: option.value, label: option.label })
    this.syncClear()
    // setAttribute 已触发 update 同步（同值也会触发 attributeChangedCallback，Chromium 实测）；随后同步表单值与校验
    this.syncFormValue()
    this.syncValidity()
  }

  /** clearable：清空输入与 value 并派发 oas-clear（detail 为清空前的 value）+ oas-change（空值） */
  private clearValue(): void {
    if (this.injectDisabled()) return
    // 用户清空置脏：reset 应恢复清空前的基线（对齐原生「用户交互不改默认值」）
    this.valueDirty = true
    const prev = this.getAttr('value', '')
    this.clearDebounce()
    this.query = ''
    this.renderDropdown(false)
    this.removeAttribute('value')
    if (this.input) {
      this.input.value = ''
      this.input.focus()
    }
    this.emit('clear', { value: prev })
    this.emit('change', { value: '', label: '' })
    this.syncClear()
    // value 属性原本不在场时 removeAttribute 不触发 update，兜底同步
    this.syncFormValue()
    this.syncValidity()
  }
}
